import {
	type DataFunctionArgs,
	json,
	type V2_MetaFunction,
} from '@remix-run/node'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { conform, useForm } from '@conform-to/react'
import { z } from 'zod'
import { redirectWithToast } from '~/utils/flash-session.server.ts'
import {
	useActionData,
	useNavigation,
	useFormAction,
	Form,
} from '@remix-run/react'
import { ErrorList, Field, TextareaField } from '~/components/forms.tsx'
import { StatusButton } from '~/components/ui/status-button.tsx'
import { sendEmail } from '~/utils/email.server.ts'
import { ContactUsEmail } from '~/routes/contact/contactemail.server.tsx'

const ACTION = '/resources/apply-form'

const ContactFormSchema = z.object({
	name: z.string().min(2, 'Name must be at least 2 characters'),
	email: z.string().email('Invalid email address'),
	birthdate: z.string(),
	message: z.string().min(10, 'Message must be at least 10 characters'),
})

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	const submission = await parse(formData, {
		schema: ContactFormSchema.superRefine(async (data, ctx) => {}),
		acceptMultipleErrors: () => true,
		async: true,
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}
	const { name, email, message, birthdate } = submission.value

	const response = await sendEmail({
		to: 'info@studyworkpray.org',
		subject: `VIA NOVA - Apply Submission from ${name}`,
		react: (
			<ContactUsEmail
				email={email}
				name={name}
				message={`Birthdate: ${birthdate}
${message}`}
			/>
		),
	})

	// const session = await signup({ email, username, password, name })
	if (response.status === 'success') {
		return redirectWithToast('/', {
			title: 'Success!',
			description: 'Your message has been sent.',
		})
	} else {
		submission.error[''] = response.error.message
		return json({ status: 'error', submission } as const, { status: 500 })
	}
}

export const meta: V2_MetaFunction = () => {
	return [{ title: 'Contact Us :: VIA NOVA' }]
}

export default function ApplyForm() {
	const actionData = useActionData<typeof action>()
	const navigation = useNavigation()
	const formAction = useFormAction()

	const [form, fields] = useForm({
		id: 'apply',
		constraint: getFieldsetConstraint(ContactFormSchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: ContactFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<Form
			method="POST"
			action={ACTION}
			className="mx-auto min-w-[368px] max-w-lg "
			{...form.props}
		>
			<Field
				labelProps={{ htmlFor: fields.name.id, children: 'Name' }}
				inputProps={{
					...conform.input(fields.name),
					autoComplete: 'name',
					tabIndex: 1,
					// autoFocus:
					// 	typeof actionData === 'undefined' ||
					// 	typeof fields.name.initialError !== 'undefined',
				}}
				errors={fields.name.errors}
			/>
			<Field
				labelProps={{ htmlFor: fields.email.id, children: 'Email' }}
				inputProps={{
					...conform.input(fields.email),
					autoComplete: 'email',
					tabIndex: 2,
					// autoFocus:
					// 	typeof actionData === 'undefined' ||
					// 	typeof fields.email.initialError !== 'undefined',
				}}
				errors={fields.email.errors}
			/>
			<Field
				labelProps={{ htmlFor: fields.birthdate.id, children: 'Birthdate' }}
				inputProps={{
					...conform.input(fields.birthdate),
					autoComplete: 'birthdate',
					type: 'date',
					tabIndex: 3,
					// autoFocus:
					// 	typeof actionData === 'undefined' ||
					// 	typeof fields.birthdate.initialError !== 'undefined',
				}}
				errors={fields.birthdate.errors}
			/>
			<TextareaField
				errors={fields.message.errors}
				labelProps={{
					htmlFor: fields.message.id,
					children: 'How did you hear about us?',
				}}
				textareaProps={{
					'aria-rowspan': 5,
					tabIndex: 4,
					...conform.textarea(fields.message),
				}}
			/>
			<ErrorList errors={[...form.errors]} id={form.errorId} />

			<div className="flex items-center justify-between gap-6">
				<StatusButton
					className="w-full"
					size="wide"
					tabIndex={5}
					status={
						navigation.state === 'submitting' &&
						navigation.formAction === formAction &&
						navigation.formMethod === 'POST'
							? 'pending'
							: actionData?.status ?? 'idle'
					}
					type="submit"
					disabled={navigation.state !== 'idle'}
				>
					Apply
				</StatusButton>
			</div>
		</Form>
	)
}
